import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import { useAuth } from '@/features/auth';
import type { Task, TaskFilters } from '../types';
import { canCreateAdjustment } from '../utils';
import { TasksFilterBar } from './TasksFilterBar';
import { TaskExpandedRow } from './TaskExpandedRow';

interface TasksDataTableProps {
  data: Task[];
  columns: ColumnDef<Task>[];
  filters: TaskFilters;
  updateFilter: <K extends keyof TaskFilters>(key: K, value: TaskFilters[K], resetPage?: boolean) => void;
  updateFilters: (updates: Partial<TaskFilters>, resetPage?: boolean) => void;
  isLoading?: boolean | undefined;
  isError?: boolean | undefined;
  error?: string | undefined;
  expandedTasks?: Record<number, boolean> | undefined;
  onClearFilters?: () => void;
  defaultFiltersExpanded?: boolean;
  hideAssignedTo?: boolean;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

const DEFAULT_EXPANDED_TASKS: Record<number, boolean> = {};

// Columns hidden by default on the task boards. The visible set (id, actions,
// status, escalated, assignedTo, result, description, matter/case, client,
// assignedBy, runTime) is everything not listed here; users can re-enable any of
// these from the Columns dropdown.
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  comment: false,
  dueAt: false,
  contract: false,
  type: false,
  timeEstimate: false,
  billableDurationInSeconds: false,
  durationInSeconds: false,
};

export function TasksDataTable({
  data,
  columns,
  filters,
  updateFilter,
  updateFilters,
  isLoading,
  isError,
  error,
  expandedTasks = DEFAULT_EXPANDED_TASKS,
  onClearFilters,
  defaultFiltersExpanded = true,
  hideAssignedTo = false,
  controlsContainer,
  tableZoom: tableZoomProp,
}: TasksDataTableProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canSeeAssignedTo = user?.role?.type === 'MD' || user?.role?.type === 'MANAGER';
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('new-tasks', DEFAULT_COLUMN_VISIBILITY);
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [rowSelection, setRowSelection] = React.useState({});
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const [searchValue, setSearchValue] = React.useState(filters.s ?? '');
  const debouncedSearch = useDebounce(searchValue, 300);
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const trimmed = debouncedSearch.trim();
    const newValue = trimmed.length >= 2 ? trimmed : undefined;
    if (newValue !== filters.s) {
      updateFilter('s', newValue, true);
    }
  }, [debouncedSearch]);

  const [idValue, setIdValue] = React.useState(filters.id?.toString() ?? '');
  const debouncedId = useDebounce(idValue, 300);
  const isFirstIdRender = React.useRef(true);

  React.useEffect(() => {
    if (isFirstIdRender.current) {
      isFirstIdRender.current = false;
      return;
    }
    const num = Number.parseInt(debouncedId.trim(), 10);
    const newValue = Number.isNaN(num) || num <= 0 ? undefined : num;
    if (newValue !== filters.id) {
      updateFilter('id', newValue, true);
    }
  }, [debouncedId]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: { columnVisibility, rowSelection },
  });

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filters.id) count++;
    if (filters['createdAt.min'] || filters['createdAt.max']) count++;
    if (filters['dueAt.min'] || filters['dueAt.max']) count++;
    if (filters.createdById) count++;
    if (filters.matterId) count++;
    if (filters.typeId) count++;
    if (filters.s) count++;
    if (!hideAssignedTo && canSeeAssignedTo && filters.assignedStaffId) count++;
    return count;
  }, [filters, hideAssignedTo, canSeeAssignedTo]);

  const handleClearFilters = () => {
    setSearchValue('');
    setIdValue('');
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-end gap-2 px-2">
        {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
        <TableHeaderControls container={controlsContainer}>
          <ColumnVisibility table={table} />
          <FilterToggleButton
            activeFilterCount={activeFilterCount}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((v) => !v)}
          />
        </TableHeaderControls>
      </div>

      <TasksFilterBar
        open={filtersOpen}
        filters={filters}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        idValue={idValue}
        onIdChange={setIdValue}
        updateFilter={updateFilter}
        updateFilters={updateFilters}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        defaultFiltersExpanded={defaultFiltersExpanded}
        hideAssignedTo={hideAssignedTo}
      />

      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-destructive">
                  {error || t('errorLoadingTasks')}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const task = row.original;
                const isExpanded = expandedTasks[task.id] ?? false;

                return (
                  <React.Fragment key={row.id}>
                    <TableRow data-state={row.getIsSelected() && 'selected'}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isExpanded && (
                      <TaskExpandedRow
                        task={task}
                        colSpan={columns.length}
                        currentUserId={user?.id}
                        canDeleteAdjustment={
                          user?.role?.type === 'MD'
                            ? task.newStatus !== 'DONE' &&
                              task.newStatus !== 'BILLED'
                            : canCreateAdjustment(
                                user?.role?.type,
                                task.newStatus,
                              )
                        }
                        isMd={user?.role?.type === 'MD'}
                      />
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('noDataAvailable')}</h3>
                    <p className="text-[#64748b]">{t('trackDataToBetterManage')}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
